import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { endpoints } from 'environments/endpoints';
import { ExternalAuditBatch } from 'app/shared/models/audits/external-audit/external-audit-batch.model';
import { ExternalAuditBatchListDto } from 'app/shared/models/audits/external-audit/external-audit-batch-list-dto.model';
import { ExternalAuditBatchDetails } from 'app/shared/models/audits/external-audit/external-audit-batch-details.model';
import { SAVRequestDto } from 'app/shared/models/audits/external-audit/sav-request-dto.model';
import {
  BatchStatusKey,
  ExternalAuditBatchStatus,
  ExternalAuditStatus
} from 'app/shared/enumerations/statuses.enum';
import { HttpHeadersUtil } from 'app/shared/utils/http-headers.util';

@Injectable({ providedIn: 'root' })
export class ExternalAuditBatchService {
  private baseUrl = environment.GATEWAY_API + endpoints.externalAuditBatch.baseRoute;

  constructor(private readonly _httpClient: HttpClient) {}

  // Returns a list DTO that contains the page of batches and a global totalCount ("Tous")
  getAllByStatus(
    status: BatchStatusKey,
    batchId?: string | null
  ): Observable<ExternalAuditBatchListDto> {
    const url = `${this.baseUrl}${endpoints.externalAuditBatch.subRoutes.getAllByStatus}${status}`;
    const params: any = {};
    if (batchId !== undefined && batchId !== null) {
      params.batchId = batchId;
    }

    return this._httpClient.get<ExternalAuditBatchListDto>(url, { params });
  }

  getDirectBatchesFromAcep(): Observable<ExternalAuditBatchDetails[]> {
    return this._httpClient.get<ExternalAuditBatchDetails[]>(
      `${environment.GATEWAY_API}${endpoints.dossiers.baseRoute}${endpoints.dossiers.subRoutes.getExternalAuditBatchDetails}`,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  add(): Observable<ExternalAuditBatch> {
    const url = `${this.baseUrl}${endpoints.externalAuditBatch.subRoutes.add}`;
    return this._httpClient.post<ExternalAuditBatch>(
      url,
      null,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  delete(dto: ExternalAuditBatch): Observable<void> {
    return this._httpClient.put<void>(
      `${this.baseUrl}${endpoints.externalAuditTempBatch.subRoutes.delete}`,
      dto,
      HttpHeadersUtil.prepareHeadersWithContentType('application/json')
    );
  }

  deleteOperation(batchId: string, operationId: string): Observable<void> {
    return this._httpClient.delete<void>(
      `${this.baseUrl}${endpoints.externalAuditBatch.subRoutes.deleteOperation}${batchId}/${operationId}`
    );
  }

  update(batch: ExternalAuditBatch): Observable<ExternalAuditBatch> {
    return this._httpClient.put<ExternalAuditBatch>(
      `${this.baseUrl}${endpoints.externalAuditBatch.subRoutes.update}`,
      batch,
      HttpHeadersUtil.prepareHeadersWithContentType('application/json')
    );
  }

  updateExternalAuditBatchStatus(
    batchId: string,
    status: ExternalAuditBatchStatus
  ): Observable<void> {
    return this._httpClient.post<void>(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.updateExternalAuditBatchStatus}${batchId}`,
      JSON.stringify(status),
      HttpHeadersUtil.prepareHeadersWithContentType('application/json')
    );
  }

  closeBatch(batchId: string): Observable<void> {
    return this._httpClient.put<void>(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.closeBatch}${batchId}`,
      null,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  generateCommandFile(batchId: string): Observable<any> {
    return this._httpClient.get(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.generateCommandFile}${batchId}`,
      { responseType: 'blob' as 'json' }
    );
  }

  getDataChanges(batchId: string): Observable<string> {
    return this._httpClient.get<string>(
      `${this.baseUrl}${endpoints.externalAuditBatch.subRoutes.getDataChanges}${batchId}`,
      HttpHeadersUtil.prepareHeadersWithContentType('application/json')
    );
  }

  loadReponse(batchId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('UploadedFile', file, file.name);

    return this._httpClient.post(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.loadReponse}${batchId}`,
      formData
    );
  }

  emitBatch(batch: ExternalAuditBatch): Observable<any> {
    return this._httpClient.put(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.emitBatch}`,
      batch,
      HttpHeadersUtil.prepareHeadersWithContentType('application/json')
    );
  }

  rejectOperation(batchId: string, operationId: string, request: { refusalReasonId: string; refusalComment?: string | null }): Observable<void> {
    return this._httpClient.post<void>(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.rejectOperation}${batchId}/${operationId}`,
      request,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  updateOperationStatus(
    batchId: string,
    operationId: string,
    newStatus: ExternalAuditStatus,
    afterSalesTypeId?: string
  ): Observable<any> {
    return this._httpClient.post(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.updateOperationStatus}${batchId}/${operationId}`,
      { status: newStatus, afterSalesTypeId: afterSalesTypeId },
      HttpHeadersUtil.prepareHeadersWithContentType('application/json')
    );
  }

  requestPartnerSAV(batchId: string, operationId: string, dto: SAVRequestDto): Observable<Blob> {
    const formData = new FormData();
    if (dto.uploadedFileRapport) formData.append('uploadedFileRapport', dto.uploadedFileRapport);
    if (dto.existingReportVersionId) formData.append('existingReportVersionId', dto.existingReportVersionId);
    if (dto.request) formData.append('request', dto.request);
    if (dto.correctiveActions) formData.append('correctiveActions', dto.correctiveActions);
    if (dto.nonSatisfactionTypeId) {
      formData.append('nonSatisfactionTypeId', dto.nonSatisfactionTypeId);
    }
    formData.append('noInterventionSheetRequired', String(dto.noInterventionSheetRequired));
    formData.append('noControlReportRequired', String(dto.noControlReportRequired));

    if (dto.timelineAttachments) {
      dto.timelineAttachments.forEach(file => formData.append('timelineAttachments', file));
    }
    // IMPORTANT : ne pas définir Content-Type
    return this._httpClient.post(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.requestPartnerSAV}${batchId}/${operationId}`,
      formData,
      { responseType: 'blob' }
    );
  }

  getNSInformationRequests(batchId: string, operationId: string): Observable<string> {
    return this._httpClient.get<string>(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.getNSInformationRequests}${batchId}/${operationId}`,
      { responseType: 'text' as 'json' }
    );
  }

  generateExternalAuditBatchesFile(): Observable<Blob> {
    return this._httpClient.get(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.generateExternalAuditBatchesFile}`,
      {
        responseType: 'blob'
      }
    );
  }

  abandonBatch(batchId: string): Observable<{ success: boolean }> {
    return this._httpClient.put<{ success: boolean }>(
      `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.abandonBatch}${batchId}`,
      null,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  // Compat: keep a PascalCase entry point for existing usages in the UI layer.
  AbandonBatch(batchId: string): Observable<{ success: boolean }> {
    return this.abandonBatch(batchId);
  }

  /**
   * Attach operations to a batch (API: AttachOperationsToBatch)
   * @param batchId Batch GUID
   * @param operationIds Array of operation GUIDs
   */
  attachOperationsToBatch(batchId: string, operationIds: string[]): Observable<any> {
    // API expects batchId as query param, operationIds as body (array of GUIDs)
    const url = `${environment.GATEWAY_API}${endpoints.externalAuditBatch.baseRoute}${endpoints.externalAuditBatch.subRoutes.attachOperationsToBatch}${batchId}`;
    return this._httpClient.post(url, operationIds, HttpHeadersUtil.prepareDefaultHeaders());
  }
}

